import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface HeroSectionProps {
  featured: Article;
  hot: Article[];
}

export default function HeroSection({ featured, hot }: HeroSectionProps) {
  return (
    <section aria-labelledby="hero-heading" className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-12">
        <div className="flex flex-col gap-3 border-b border-background-200 pb-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-500" />
              往期热料 · 一次翻清
            </p>
            <h1
              id="hero-heading"
              className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl"
            >
              看料88：把散落各处的娱乐话题，按时间线存档整理
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              综艺名场面、影视口碑、创作者动态与网络热议，这里按发布时间归档整理。想回顾往期爆料的话题走向，从这一页往下翻就够了。
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link
              to="/archive"
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              进入往期存档
            </Link>
            <Link
              to="/faq"
              className="whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              了解收录标准
            </Link>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <article className="group lg:col-span-8">
            <Link to={`/article/${featured.id}`} className="block">
              <div className="relative h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80 lg:h-[22rem]">
                <img
                  src={featured.cover}
                  alt={`${featured.title} 封面图`}
                  title={`${featured.title} - 看料88 头条存档`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-bookmark-3-line" />
                  本周头条
                </span>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                  <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-800">
                    {featured.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {featured.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    约 {featured.readingTime} 分钟
                  </span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <i className="ri-fire-line" />
                    热度 {featured.heat}
                  </span>
                </div>
                <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                  {featured.title}
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {featured.summary}
                </p>
              </div>
            </Link>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  今日热榜
                </h2>
                <Link to="/archive" className="text-xs text-foreground-500 transition-colors hover:text-primary-600">
                  全部
                </Link>
              </div>
              <ol className="mt-4 flex flex-col divide-y divide-background-200">
                {hot.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/article/${item.id}`}
                      className="flex items-start gap-3 py-3 transition-colors hover:text-primary-600"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold ${
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium leading-snug text-foreground-900">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-500">
                          {item.category} · {item.dateLabel}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}